/**
 * Build-time environment selection for ui-internal.
 *
 * **There is only `prod`.** This app has one environment, not the fleet's
 * four. An internal console serves the same employees whatever the deploy
 * says, and a second copy of it would be a second thing to keep in step for no
 * reader's benefit.
 *
 * The `VITE_DEPLOY_ENV` seam stays, with one member. The root
 * `cloudbuild.yaml` is shaped like every other repo's — one file, `_ENV` from
 * the trigger — and keeping that shape is what makes adding an environment
 * later an edit here rather than a rewrite. An unknown value fails loudly,
 * which is the point of the seam: a bundle aimed at the wrong project is worse
 * than a build that stops.
 *
 * The Firebase config is transcribed from auth-service `config/config.js`,
 * prod block. It is per-PROJECT, not per-app, so ui-internal and super-ui read
 * the same values. All of it is public client configuration, not secrets — a
 * Firebase web apiKey identifies the project to Identity Platform and grants
 * nothing on its own.
 *
 * The API base URL is NOT here. `src/lib/api.ts` reads `VITE_API_BASE_URL`.
 * The merge pipeline sets it from the live gateway hostname when it builds
 * the frontend. Locally, `scripts/dev.sh` sets it to the backend on 8080.
 * This table must not guess a host: a stale URL would look like configuration
 * and behave like a bug.
 */

export type DeployEnv = 'prod'

export interface FirebaseWebConfig {
  apiKey: string
  authDomain: string
  projectId: string
  storageBucket: string
}

export interface EnvConfig {
  deployEnv: DeployEnv
  appHostname: string
  firebase: FirebaseWebConfig
  /**
   * Identity Platform tenant for internal staff.
   *
   * **The same tenant in every environment: the prod super tenant.** An
   * employee is one person, not one per deploy environment, so a dev build of
   * this console authenticates the same real people a prod build does.
   *
   * A tenant belongs to a project, which is why `firebase` above is
   * blytzpay-prod's in every environment too. Auth is not per-environment
   * here; only the app's own deploy is.
   *
   * Still typed nullable. `isAuthProvisioned` is the gate that withholds the
   * sign-in control, and keeping the null case reachable keeps that gate and
   * its screen honest rather than dead code.
   */
  tenantId: string | null
}

/**
 * The one Identity Platform the console signs in against.
 *
 * blytzpay-prod, in every environment, because that is where the super tenant
 * lives and staff accounts exist once. Copied from blytz-platform
 * `auth-service/config/config.js`, prod block — change it only when that file
 * changes.
 */
const AUTH_FIREBASE: FirebaseWebConfig = {
  apiKey: 'AIzaSyDMia9tCbypXofboUPCmhnN3QB_2ZnmEkI',
  authDomain: 'blytzpay-prod.firebaseapp.com',
  projectId: 'blytzpay-prod',
  storageBucket: 'blytzpay-prod.firebasestorage.app'
}

/** The super tenant in blytzpay-prod. Shared with ui-super. */
const SUPER_TENANT_ID = 'super-gjjlg'

const ENVS: Record<DeployEnv, EnvConfig> = {
  prod: {
    deployEnv: 'prod',
    appHostname: 'internal.blytzpay.com',
    firebase: AUTH_FIREBASE,
    tenantId: SUPER_TENANT_ID
  }
}

/**
 * Resolve the config for a deploy environment.
 *
 * Unknown or absent values fail loudly rather than defaulting to an
 * environment: silently falling back to dev would point a stage bundle at the
 * dev tenant, and a tenant mismatch surfaces as a confusing auth failure
 * rather than an obvious misconfiguration.
 *
 * @param raw - The `VITE_DEPLOY_ENV` value to resolve.
 * @returns The matching environment config.
 */
export function resolveEnv(raw: string | undefined): EnvConfig {
  if (raw !== undefined && Object.hasOwn(ENVS, raw)) {
    return ENVS[raw as DeployEnv]
  }
  throw new Error(
    `VITE_DEPLOY_ENV must be one of ${Object.keys(ENVS).join(', ')} (got: ${raw ?? 'undefined'})`
  )
}

/**
 * Whether this environment can actually authenticate anyone.
 *
 * The sign-in screen asks before offering the Google control. Firebase would
 * happily initialize against a null tenant and reject at the popup, which
 * reads to the operator as "sign-in is broken" rather than "sign-in is not
 * built yet".
 *
 * @param config - The environment to check.
 * @returns True once the tenant is provisioned.
 */
export function isAuthProvisioned(config: EnvConfig): boolean {
  return config.tenantId !== null
}

export const env: EnvConfig = resolveEnv(import.meta.env.VITE_DEPLOY_ENV)
